import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart3, TrendingUp, Users, Building2, CheckCircle, XCircle, Clock, FileText, Shield, ArrowLeft, Filter, Eye, Loader2, Download, UserPlus, Upload } from 'lucide-react';
import type { User } from '../App';
import type { InnovationSubmission } from '../api/innovations';
import { listSubmissions, getSubmission, downloadDocument, uploadDocuments } from '../api/innovations';
import {
  approveReviewerFeedback,
  requestReviewerFeedbackChanges,
  approveFilingTransition,
  rejectFilingTransition,
  recordFilingDocPrepBillingCleared,
  setFilingDocumentationPackage,
} from '../api/admin';
import { getFilingRequestReport } from '../api/admin';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

type DrillDownView = 'overview' | 'pending-assessment' | 'pending-feedback-approval' | 'assessment-completed' | 'ip-filed';

const CATEGORIES = ['Tech', 'Health', 'Education', 'Manufacturing', 'Sustainability', 'General'];
const FEEDBACK_STATUS_LABELS: Record<'pending_superadmin_review' | 'approved' | 'changes_requested', string> = {
  pending_superadmin_review: 'Pending Superadmin Approval',
  approved: 'Approved and Sent to Innovator',
  changes_requested: 'Changes Requested from Reviewer',
};
type FilingStatus = 'received' | 'in_support' | 'ready_to_file' | 'closed';
const FILING_STATUS_LABELS: Record<FilingStatus, string> = {
  received: 'Received',
  in_support: 'In Support',
  ready_to_file: 'Ready to File',
  closed: 'Closed',
};

export function AdminDashboard({ user, onReviewClick, onNavigateToAssignments, onNavigateToInstitutionApplications, isSuperAdmin }: { user: User; onReviewClick?: (id: string) => void; onNavigateToAssignments?: () => void; onNavigateToInstitutionApplications?: () => void; isSuperAdmin?: boolean }) {
  const [submissions, setSubmissions] = useState<InnovationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<DrillDownView>('overview');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [documentsModalSubmission, setDocumentsModalSubmission] = useState<InnovationSubmission | null>(null);
  const [documentsModalLoading, setDocumentsModalLoading] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [addDocFile, setAddDocFile] = useState<File | null>(null);
  const [addDocLoading, setAddDocLoading] = useState(false);
  const [addDocError, setAddDocError] = useState<string | null>(null);
  const [feedbackActionLoading, setFeedbackActionLoading] = useState(false);
  const [filingDocPrepLoading, setFilingDocPrepLoading] = useState(false);
  const [filingPackageUrlInput, setFilingPackageUrlInput] = useState('');
  const [filingSummary, setFilingSummary] = useState<Record<FilingStatus, number>>({
    received: 0,
    in_support: 0,
    ready_to_file: 0,
    closed: 0,
  });
  const [recentFilingUpdates, setRecentFilingUpdates] = useState<Array<{ submission_uuid: string; title: string; filing_request_status: FilingStatus }>>([]);
  const [pendingTransitionApprovals, setPendingTransitionApprovals] = useState(0);

  useEffect(() => {
    if (!documentsModalSubmission || !documentsModalLoading) return;
    let cancelled = false;
    getSubmission(documentsModalSubmission.submission_uuid)
      .then((data) => {
        if (!cancelled) {
          setDocumentsModalSubmission(data);
          setDocumentsModalLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setDocumentsModalLoading(false);
      });
    return () => { cancelled = true; };
  }, [documentsModalSubmission?.submission_uuid, documentsModalLoading]);

  const openDocumentsModal = useCallback((sub: InnovationSubmission) => {
    setDocumentsModalSubmission(sub);
    setDocumentsModalLoading(true);
  }, []);
  const closeDocumentsModal = useCallback(() => {
    setDocumentsModalSubmission(null);
    setDocumentsModalLoading(false);
    setDownloadingDocId(null);
    setAddDocFile(null);
    setAddDocError(null);
    setFilingPackageUrlInput('');
  }, []);

  useEffect(() => {
    const u = documentsModalSubmission?.filing_documentation_prep?.package_url;
    setFilingPackageUrlInput(typeof u === 'string' ? u : '');
  }, [documentsModalSubmission?.submission_uuid, documentsModalSubmission?.filing_documentation_prep?.package_url]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdminRole = user?.role === 'super_admin';
  const feedbackStatusLabel = (status?: InnovationSubmission['feedback_approval_status']) =>
    status ? FEEDBACK_STATUS_LABELS[status] : 'Not yet submitted by reviewer';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [res, filingRes] = await Promise.all([
          listSubmissions(1, 200),
          getFilingRequestReport({ status: 'all' }),
        ]);
        if (!cancelled) {
          setSubmissions(res.submissions || []);
          const nextSummary: Record<FilingStatus, number> = { received: 0, in_support: 0, ready_to_file: 0, closed: 0 };
          for (const row of filingRes.summary || []) {
            if (row.filing_request_status in nextSummary) {
              nextSummary[row.filing_request_status as FilingStatus] = row.total;
            }
          }
          setFilingSummary(nextSummary);
          setRecentFilingUpdates((filingRes.items || []).slice(0, 5).map((item) => ({
            submission_uuid: item.submission_uuid,
            title: item.title,
            filing_request_status: item.filing_request_status as FilingStatus,
          })));
          setPendingTransitionApprovals((filingRes.items || []).filter((item) => !!item.filing_transition_requested_status).length);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load submissions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalSubmissions = submissions.length;
  const pendingAssessments = submissions.filter((s) => s.status === 'submitted' || s.status === 'under_review').length;
  const pendingFeedbackApprovals = submissions.filter(
    (s) => s.status === 'under_review' && s.feedback_approval_status === 'pending_superadmin_review'
  ).length;
  const completedAssessments = submissions.filter((s) => s.status === 'completed').length;
  const rejectedCount = submissions.filter((s) => s.status === 'rejected').length;
  const ipFiled = 0;
  const institutionalSubmissions = submissions.filter((s) => s.institution_uuid != null).length;
  const individualSubmissions = submissions.filter((s) => s.institution_uuid == null).length;

  const categoryStats = useMemo(() => {
    return CATEGORIES.map((cat) => {
      const total = submissions.filter((s) => (s.technology_area || 'General') === cat).length;
      const completed = submissions.filter((s) => (s.technology_area || 'General') === cat && s.status === 'completed').length;
      const rejected = submissions.filter((s) => (s.technology_area || 'General') === cat && s.status === 'rejected').length;
      const pending = submissions.filter((s) => (s.technology_area || 'General') === cat && (s.status === 'submitted' || s.status === 'under_review')).length;
      return { category: cat, total, completed, rejected, pending };
    }).filter((s) => s.total > 0);
  }, [submissions]);

  const getFilteredSubmissions = () => {
    let filtered = submissions;
    if (currentView === 'pending-assessment') {
      filtered = filtered.filter((s) => s.status === 'submitted' || s.status === 'under_review');
    } else if (currentView === 'pending-feedback-approval') {
      filtered = filtered.filter(
        (s) => s.status === 'under_review' && s.feedback_approval_status === 'pending_superadmin_review'
      );
    } else if (currentView === 'assessment-completed') {
      filtered = filtered.filter((s) => s.status === 'completed');
    } else if (currentView === 'ip-filed') {
      filtered = [];
    }
    if (filterCategory !== 'all') {
      filtered = filtered.filter((s) => (s.technology_area || 'General') === filterCategory);
    }
    return filtered;
  };

  const filteredSubmissions = getFilteredSubmissions();

  const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '—');

  if (currentView !== 'overview') {
    const viewTitles: Record<string, string> = {
      'pending-assessment': 'Pending Assessment',
      'pending-feedback-approval': 'Pending Feedback Approval',
      'assessment-completed': 'Assessment Completed',
      'ip-filed': 'Intellectual Property Filed',
    };
    const viewDescriptions: Record<string, string> = {
      'pending-assessment': 'Innovations awaiting reviewer evaluation',
      'pending-feedback-approval': 'Reviewer feedback awaiting superadmin approval before release to innovators',
      'assessment-completed': 'Innovations that have been assessed',
      'ip-filed': 'Innovations with IP filing requests',
    };
    return (
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => {
                setCurrentView('overview');
                setFilterCategory('all');
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Overview
            </Button>
            <div>
              <h1 className="text-slate-900 mb-1">{viewTitles[currentView]}</h1>
              <p className="text-slate-600">{viewDescriptions[currentView]}</p>
            </div>
          </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-600">Category</label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Results ({filteredSubmissions.length})</CardTitle>
            <CardDescription>Real data from registry</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Submission Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Workflow Update</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubmissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                      No innovations match the current filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubmissions.map((sub) => (
                    <TableRow key={sub.submission_uuid}>
                      <TableCell className="text-slate-900">{sub.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{sub.technology_area || 'General'}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 capitalize">{sub.current_state}</TableCell>
                      <TableCell className="text-slate-600">{formatDate(sub.submission_date)}</TableCell>
                      <TableCell>
                        <Badge variant={sub.status === 'completed' ? 'default' : 'secondary'}>{sub.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline">
                            {feedbackStatusLabel(sub.feedback_approval_status)}
                          </Badge>
                          {sub.filing_request_status && sub.filing_request_status !== 'none' && (
                            <Badge variant="secondary">
                              Filing: {sub.filing_request_status.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                            onClick={() => onReviewClick?.(sub.submission_uuid)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDocumentsModal(sub)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View & documents
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={!!documentsModalSubmission} onOpenChange={(open) => !open && closeDocumentsModal()}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Submission details & documents
              </DialogTitle>
              <DialogDescription>
                {documentsModalLoading ? 'Loading…' : documentsModalSubmission ? documentsModalSubmission.title : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 min-h-0 space-y-4 pr-2">
              {documentsModalLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
                </div>
              ) : documentsModalSubmission ? (
                <>
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-700">Submission details</h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Abstract</span>
                        <p className="text-slate-900 mt-0.5 whitespace-pre-wrap">{documentsModalSubmission.abstract}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-slate-500">Stage</span>
                          <p className="text-slate-900 capitalize">{documentsModalSubmission.current_state}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Category</span>
                          <p className="text-slate-900">{documentsModalSubmission.technology_area || '—'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Status</span>
                          <p className="text-slate-900">{documentsModalSubmission.status}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Submitted</span>
                          <p className="text-slate-900">{formatDate(documentsModalSubmission.submission_date)}</p>
                        </div>
                      </div>
                      {documentsModalSubmission.keywords && documentsModalSubmission.keywords.length > 0 && (
                        <div>
                          <span className="text-slate-500">Keywords</span>
                          <p className="text-slate-900">{documentsModalSubmission.keywords.join(', ')}</p>
                        </div>
                      )}
                      {documentsModalSubmission.expected_outcome && (
                        <div>
                          <span className="text-slate-500">Expected outcome</span>
                          <p className="text-slate-900 whitespace-pre-wrap">{documentsModalSubmission.expected_outcome}</p>
                        </div>
                      )}
                      {documentsModalSubmission.ip_protection_recommendation && (
                        <div className="pt-2 border-t border-slate-200">
                          <span className="text-slate-500">Reviewer feedback draft</span>
                          <p className="text-slate-900 whitespace-pre-wrap mt-1">{documentsModalSubmission.ip_protection_recommendation}</p>
                          {documentsModalSubmission.recommended_ip_types && documentsModalSubmission.recommended_ip_types.length > 0 && (
                            <p className="text-slate-700 mt-1 text-sm">
                              Recommended Intellectual Property types: {documentsModalSubmission.recommended_ip_types.join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                      {documentsModalSubmission.comprehensive_report_url && (
                        <div className="pt-2 border-t border-slate-200">
                          <span className="text-slate-500">Comprehensive report (external link)</span>
                          <a
                            href={documentsModalSubmission.comprehensive_report_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--royal)] underline break-all block mt-1"
                          >
                            {documentsModalSubmission.comprehensive_report_url}
                          </a>
                        </div>
                      )}
                      <div className="pt-1">
                        <span className="text-slate-500">Feedback approval status</span>
                        <p className="text-slate-900">
                          {feedbackStatusLabel(documentsModalSubmission.feedback_approval_status)}
                        </p>
                        {documentsModalSubmission.feedback_rejection_reason && (
                          <p className="text-amber-700 text-sm mt-1">
                            Changes requested reason: {documentsModalSubmission.feedback_rejection_reason}
                          </p>
                        )}
                      </div>
                      {documentsModalSubmission.filing_transition_requested_status && (
                        <div className="pt-2 border-t border-slate-200">
                          <span className="text-slate-500">Pending filing transition request</span>
                          <p className="text-slate-900">
                            {documentsModalSubmission.filing_transition_requested_status.replace(/_/g, ' ')}
                          </p>
                          {documentsModalSubmission.filing_transition_rejection_reason && (
                            <p className="text-rose-700 text-sm mt-1">
                              Last rejection reason: {documentsModalSubmission.filing_transition_rejection_reason}
                            </p>
                          )}
                          {isSuperAdminRole && (
                            <div className="pt-2 flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                disabled={feedbackActionLoading}
                                onClick={async () => {
                                  if (!documentsModalSubmission) return;
                                  setFeedbackActionLoading(true);
                                  try {
                                    await approveFilingTransition(documentsModalSubmission.submission_uuid);
                                    const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                                    setDocumentsModalSubmission(updated);
                                    setSubmissions((prev) => prev.map((s) => s.submission_uuid === updated.submission_uuid ? updated : s));
                                    alert('Filing transition approved.');
                                  } catch (e: unknown) {
                                    const msg =
                                      e && typeof e === 'object' && 'message' in e
                                        ? String((e as { message: unknown }).message)
                                        : 'Failed to approve filing transition';
                                    alert(msg);
                                  } finally {
                                    setFeedbackActionLoading(false);
                                  }
                                }}
                              >
                                Approve Filing Transition
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={feedbackActionLoading}
                                onClick={async () => {
                                  if (!documentsModalSubmission) return;
                                  const reason = window.prompt('Reason for rejecting filing transition (optional):') || undefined;
                                  setFeedbackActionLoading(true);
                                  try {
                                    await rejectFilingTransition(documentsModalSubmission.submission_uuid, reason);
                                    const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                                    setDocumentsModalSubmission(updated);
                                    setSubmissions((prev) => prev.map((s) => s.submission_uuid === updated.submission_uuid ? updated : s));
                                    alert('Filing transition request rejected.');
                                  } catch (e: unknown) {
                                    const msg =
                                      e && typeof e === 'object' && 'message' in e
                                        ? String((e as { message: unknown }).message)
                                        : 'Failed to reject filing transition';
                                    alert(msg);
                                  } finally {
                                    setFeedbackActionLoading(false);
                                  }
                                }}
                              >
                                Reject Filing Transition
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      {isSuperAdminRole &&
                        documentsModalSubmission.ip_protection_recommendation &&
                        (documentsModalSubmission.feedback_approval_status || 'pending_superadmin_review') === 'pending_superadmin_review' && (
                          <div className="pt-2 border-t border-slate-200 flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              disabled={feedbackActionLoading}
                              onClick={async () => {
                                if (!documentsModalSubmission) return;
                                setFeedbackActionLoading(true);
                                try {
                                  await approveReviewerFeedback(documentsModalSubmission.submission_uuid);
                                  const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                                  setDocumentsModalSubmission(updated);
                                  setSubmissions((prev) => prev.map((s) => s.submission_uuid === updated.submission_uuid ? updated : s));
                                  alert('Feedback approved and sent to innovator.');
                                } catch (e: unknown) {
                                  const msg =
                                    e && typeof e === 'object' && 'message' in e
                                      ? String((e as { message: unknown }).message)
                                      : 'Failed to approve feedback';
                                  alert(msg);
                                } finally {
                                  setFeedbackActionLoading(false);
                                }
                              }}
                            >
                              Approve and Send to Innovator
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={feedbackActionLoading}
                              onClick={async () => {
                                if (!documentsModalSubmission) return;
                                const reason = window.prompt('Reason for changes requested (optional):') || undefined;
                                setFeedbackActionLoading(true);
                                try {
                                  await requestReviewerFeedbackChanges(documentsModalSubmission.submission_uuid, reason);
                                  const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                                  setDocumentsModalSubmission(updated);
                                  setSubmissions((prev) => prev.map((s) => s.submission_uuid === updated.submission_uuid ? updated : s));
                                  alert('Changes requested from reviewer.');
                                } catch (e: unknown) {
                                  const msg =
                                    e && typeof e === 'object' && 'message' in e
                                      ? String((e as { message: unknown }).message)
                                      : 'Failed to request reviewer changes';
                                  alert(msg);
                                } finally {
                                  setFeedbackActionLoading(false);
                                }
                              }}
                            >
                              Request Changes from Reviewer
                            </Button>
                          </div>
                        )}
                      {isSuperAdminRole &&
                        !documentsModalSubmission.institution_uuid &&
                        !documentsModalSubmission.filing_documentation_prep?.billing_cleared_at && (
                          <div className="pt-2 border-t border-slate-200 space-y-2">
                            <h4 className="text-sm font-semibold text-slate-800">Filing documentation — individual innovator</h4>
                            <p className="text-xs text-slate-600">
                              Record here once the innovator has been billed for filing-documentation preparation. They can
                              submit a preparation request in the app only after this step.
                            </p>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              disabled={filingDocPrepLoading}
                              onClick={async () => {
                                if (!documentsModalSubmission) return;
                                setFilingDocPrepLoading(true);
                                try {
                                  await recordFilingDocPrepBillingCleared(documentsModalSubmission.submission_uuid);
                                  const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                                  setDocumentsModalSubmission(updated);
                                  setSubmissions((prev) =>
                                    prev.map((s) => (s.submission_uuid === updated.submission_uuid ? updated : s))
                                  );
                                  alert('Billing recorded; the innovator may request filing documentation preparation.');
                                } catch (e: unknown) {
                                  const msg =
                                    e && typeof e === 'object' && 'message' in e
                                      ? String((e as { message: unknown }).message)
                                      : 'Failed to record billing';
                                  alert(msg);
                                } finally {
                                  setFilingDocPrepLoading(false);
                                }
                              }}
                            >
                              {filingDocPrepLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                              Record filing documentation billing cleared
                            </Button>
                          </div>
                        )}
                      {isSuperAdminRole && documentsModalSubmission.filing_documentation_prep?.requested_at && (
                        <div className="pt-2 border-t border-slate-200 space-y-3">
                          <h4 className="text-sm font-semibold text-slate-800">Filing documentation package</h4>
                          <p className="text-xs text-slate-600">
                            Only super administrators publish the prepared documentation link. Institution commercial terms
                            stay outside the application; for individual innovators, billing must already be recorded before
                            they could request preparation.
                          </p>
                          <div className="space-y-1">
                            <Label htmlFor="filing-pkg-url">Documentation package URL (https)</Label>
                            <Input
                              id="filing-pkg-url"
                              type="url"
                              className="font-mono text-sm"
                              value={filingPackageUrlInput}
                              onChange={(e) => setFilingPackageUrlInput(e.target.value)}
                              placeholder="https://…"
                            />
                          </div>
                          <Button
                            size="sm"
                            disabled={filingDocPrepLoading || !filingPackageUrlInput.trim()}
                            onClick={async () => {
                              if (!documentsModalSubmission) return;
                              setFilingDocPrepLoading(true);
                              try {
                                await setFilingDocumentationPackage(documentsModalSubmission.submission_uuid, {
                                  package_url: filingPackageUrlInput.trim(),
                                  mark_ready: true,
                                });
                                const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                                setDocumentsModalSubmission(updated);
                                setSubmissions((prev) =>
                                  prev.map((s) => (s.submission_uuid === updated.submission_uuid ? updated : s))
                                );
                                alert('Package link saved and marked ready for the client.');
                              } catch (e: unknown) {
                                const msg =
                                  e && typeof e === 'object' && 'message' in e
                                    ? String((e as { message: unknown }).message)
                                    : 'Failed to save package';
                                alert(msg);
                              } finally {
                                setFilingDocPrepLoading(false);
                              }
                            }}
                          >
                            {filingDocPrepLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Save link and mark ready for client
                          </Button>
                        </div>
                      )}
                      {(documentsModalSubmission.submitter_email || documentsModalSubmission.institution_name) && (
                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200">
                          {documentsModalSubmission.submitter_email && (
                            <div>
                              <span className="text-slate-500">Submitter</span>
                              <p className="text-slate-900">{documentsModalSubmission.submitter_email}</p>
                            </div>
                          )}
                          {documentsModalSubmission.institution_name && (
                            <div>
                              <span className="text-slate-500">Institution</span>
                              <p className="text-slate-900">{documentsModalSubmission.institution_name}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Supporting documents
                    </h4>
                    {isAdmin && (
                      <div className="mb-4 p-3 rounded border border-slate-200 bg-white space-y-2">
                        <p className="text-xs text-slate-600">Add a document to this submission (PDF, DOC, images, ZIP up to 25MB)</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.zip"
                            className="text-sm text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              setAddDocFile(f || null);
                              setAddDocError(null);
                            }}
                          />
                          <Button
                            size="sm"
                            disabled={!addDocFile || addDocLoading}
                            onClick={async () => {
                              if (!documentsModalSubmission || !addDocFile) return;
                              setAddDocLoading(true);
                              setAddDocError(null);
                              try {
                                await uploadDocuments(documentsModalSubmission.submission_uuid, [addDocFile]);
                                const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                                setDocumentsModalSubmission(updated);
                                setAddDocFile(null);
                                setSubmissions((prev) =>
                                  prev.map((s) =>
                                    s.submission_uuid === updated.submission_uuid ? updated : s
                                  )
                                );
                              } catch (e: unknown) {
                                const res = e && typeof e === 'object' && 'response' in e && (e as { response?: { data?: { message?: string } } }).response?.data;
                                setAddDocError(res?.message || (e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Upload failed'));
                              } finally {
                                setAddDocLoading(false);
                              }
                            }}
                          >
                            {addDocLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                            Add document
                          </Button>
                        </div>
                        {addDocError && <p className="text-xs text-red-600">{addDocError}</p>}
                      </div>
                    )}
                    {documentsModalSubmission.documents && documentsModalSubmission.documents.length > 0 ? (
                      <ul className="space-y-2">
                        {documentsModalSubmission.documents.map((doc) => (
                          <li
                            key={doc.doc_uuid}
                            className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                              <span className="text-sm text-slate-900 truncate" title={doc.file_name}>{doc.file_name}</span>
                              <span className="text-xs text-slate-500 flex-shrink-0">({(doc.file_size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={downloadingDocId === doc.doc_uuid}
                              onClick={async () => {
                                if (!documentsModalSubmission) return;
                                setDownloadingDocId(doc.doc_uuid);
                                try {
                                  await downloadDocument(documentsModalSubmission.submission_uuid, doc.doc_uuid, doc.file_name);
                                } catch (e: unknown) {
                                  console.error('Download failed', e);
                                  alert(e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Download failed');
                                } finally {
                                  setDownloadingDocId(null);
                                }
                              }}
                            >
                              {downloadingDocId === doc.doc_uuid ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                              Download
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="rounded border-2 border-dashed border-slate-200 bg-white flex items-center justify-center py-6 text-slate-500 text-sm">
                        No supporting documents uploaded
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div>
        <h1 className="text-[var(--navy)] font-display font-semibold mb-2">Admin Dashboard</h1>
        <p className="text-[var(--navy)]/70">
          System oversight and submission management — real data from registry
        </p>
      </div>

      {isSuperAdminRole && (
        <Card className="border-[var(--gold)]/30 bg-[var(--gold-pale)]/30">
          <CardHeader>
            <CardTitle className="text-[var(--navy)] font-display">Filing Progress Notifications</CardTitle>
            <CardDescription className="text-[var(--navy)]/70">
              Superadmin visibility into filing-request lifecycle updates.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Received: {filingSummary.received}</Badge>
              <Badge variant="outline">In Support: {filingSummary.in_support}</Badge>
              <Badge variant="outline">Ready to File: {filingSummary.ready_to_file}</Badge>
              <Badge variant="outline">Closed: {filingSummary.closed}</Badge>
              <Badge className="bg-amber-600 text-white">Pending Transition Approvals: {pendingTransitionApprovals}</Badge>
            </div>
            {recentFilingUpdates.length > 0 && (
              <div className="space-y-2">
                {recentFilingUpdates.map((item) => (
                  <div key={item.submission_uuid} className="rounded border border-slate-200 bg-white px-3 py-2 text-sm flex items-center justify-between gap-3">
                    <span className="text-slate-700 truncate">{item.title}</span>
                    <Badge variant="secondary">Filing: {FILING_STATUS_LABELS[item.filing_request_status]}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {onNavigateToAssignments && (
        <Card className="border-[var(--gold)]/30 bg-[var(--gold-pale)]/30">
          <CardContent className="flex flex-row flex-wrap items-center justify-between gap-4 py-4">
            <div>
              <h3 className="font-display font-semibold text-[var(--navy)]">Reviewer assignment</h3>
              <p className="text-sm text-[var(--navy)]/70">Assign unassigned submissions to reviewers and manage assignments</p>
            </div>
            <Button onClick={onNavigateToAssignments} className="bg-[var(--gold)] text-[var(--navy)] hover:opacity-90">
              <UserPlus className="w-4 h-4 mr-2" />
              Assignment management
            </Button>
          </CardContent>
        </Card>
      )}

      {isSuperAdmin && onNavigateToInstitutionApplications && (
        <Card className="border-[var(--gold)]/30 bg-[var(--gold-pale)]/30">
          <CardContent className="flex flex-row flex-wrap items-center justify-between gap-4 py-4">
            <div>
              <h3 className="font-display font-semibold text-[var(--navy)]">Institution applications</h3>
              <p className="text-sm text-[var(--navy)]/70">Review and approve or reject university and institution access requests</p>
            </div>
            <Button onClick={onNavigateToInstitutionApplications} className="bg-[var(--gold)] text-[var(--navy)] hover:opacity-90">
              <Building2 className="w-4 h-4 mr-2" />
              Manage applications
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
        </div>
      ) : (
        <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-[var(--navy)] font-display">Total Submissions</CardTitle>
                <FileText className="w-5 h-5 text-[var(--gold)]" />
          </CardHeader>
          <CardContent>
                <div className="text-[var(--navy)] font-semibold">{totalSubmissions}</div>
                <p className="text-[var(--navy)]/60">All innovations</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-[var(--navy)] font-display">Institutional</CardTitle>
            <Building2 className="w-5 h-5 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-slate-900">{institutionalSubmissions}</div>
            <p className="text-slate-500">From institutions</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-[var(--navy)] font-display">Pending Approval</CardTitle>
                <Shield className="w-5 h-5 text-amber-600" />
          </CardHeader>
          <CardContent>
            <button
              type="button"
              onClick={() => setCurrentView('pending-feedback-approval')}
              className="w-full text-left rounded-lg border border-transparent p-0 hover:text-[var(--royal)] transition-colors"
            >
              <div className="text-[var(--navy)] font-semibold">{pendingFeedbackApprovals}</div>
              <p className="text-[var(--navy)]/60">Waiting for superadmin decision</p>
            </button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-[var(--navy)] font-display">Individual</CardTitle>
            <Users className="w-5 h-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-slate-900">{individualSubmissions}</div>
            <p className="text-slate-500">From innovators</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
                <CardTitle className="text-[var(--navy)] font-display">Assessment Status</CardTitle>
                <CardDescription>Click a status to view list</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <button
                  type="button"
              onClick={() => setCurrentView('pending-assessment')}
              className="w-full text-left hover:bg-slate-50 p-3 rounded-lg transition-colors border border-transparent hover:border-amber-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span>Pending Assessment</span>
                </div>
                <span className="text-slate-900">{pendingAssessments}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-amber-600 h-2 rounded-full"
                      style={{ width: `${totalSubmissions ? (pendingAssessments / totalSubmissions) * 100 : 0}%` }}
                />
              </div>
            </button>
            <button
                  type="button"
              onClick={() => setCurrentView('assessment-completed')}
              className="w-full text-left hover:bg-slate-50 p-3 rounded-lg transition-colors border border-transparent hover:border-emerald-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span>Assessment Completed</span>
                </div>
                <span className="text-slate-900">{completedAssessments}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                      className="bg-[var(--royal)] h-2 rounded-full"
                      style={{ width: `${totalSubmissions ? (completedAssessments / totalSubmissions) * 100 : 0}%` }}
                />
              </div>
            </button>
            {isSuperAdminRole && (
              <button
                type="button"
                onClick={() => setCurrentView('pending-feedback-approval')}
                className="w-full text-left hover:bg-slate-50 p-3 rounded-lg transition-colors border border-transparent hover:border-amber-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Shield className="w-4 h-4 text-amber-600" />
                    <span>Pending Superadmin Approval</span>
                  </div>
                  <span className="text-slate-900">{pendingFeedbackApprovals}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-amber-600 h-2 rounded-full"
                    style={{ width: `${totalSubmissions ? (pendingFeedbackApprovals / totalSubmissions) * 100 : 0}%` }}
                  />
                </div>
              </button>
            )}
            <button
                  type="button"
              onClick={() => setCurrentView('ip-filed')}
              className="w-full text-left hover:bg-slate-50 p-3 rounded-lg transition-colors border border-transparent hover:border-blue-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-slate-600">
                  <Shield className="w-4 h-4 text-blue-600" />
                  <span>Intellectual Property Filed</span>
                </div>
                <span className="text-slate-900">{ipFiled}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                      className="bg-[var(--gold)] h-2 rounded-full"
                      style={{ width: `${totalSubmissions && ipFiled ? (ipFiled / totalSubmissions) * 100 : 0}%` }}
                />
              </div>
            </button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
                <CardTitle className="text-[var(--navy)] font-display">Category (technology area)</CardTitle>
                <CardDescription>By submission category</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Pending</TableHead>
                      <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                    {categoryStats.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-500 py-4">
                          No submissions yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      categoryStats.map((stat) => (
                    <TableRow key={stat.category}>
                      <TableCell className="text-slate-900 text-sm">{stat.category}</TableCell>
                          <TableCell><Badge variant="outline">{stat.total}</Badge></TableCell>
                          <TableCell><span className="text-sm">{stat.pending}</span></TableCell>
                          <TableCell><span className="text-sm">{stat.completed}</span></TableCell>
                    </TableRow>
                      ))
                    )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
              <CardTitle className="text-[var(--navy)] font-display">Status distribution</CardTitle>
              <CardDescription>Completed vs rejected (from registry)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span>Completed</span>
              </div>
                  <span className="text-slate-900">{completedAssessments}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                    className="bg-[var(--royal)] h-2 rounded-full"
                    style={{ width: `${totalSubmissions ? (completedAssessments / totalSubmissions) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-slate-600">
                <XCircle className="w-4 h-4 text-red-600" />
                    <span>Rejected</span>
              </div>
                  <span className="text-slate-900">{rejectedCount}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-red-600 h-2 rounded-full"
                    style={{ width: `${totalSubmissions ? (rejectedCount / totalSubmissions) * 100 : 0}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
        </>
      )}

      <Dialog open={!!documentsModalSubmission} onOpenChange={(open) => !open && closeDocumentsModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Submission details & documents
            </DialogTitle>
            <DialogDescription>
              {documentsModalLoading
                ? 'Loading…'
                : documentsModalSubmission
                  ? documentsModalSubmission.title
                  : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 space-y-4 pr-2">
            {documentsModalLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
              </div>
            ) : documentsModalSubmission ? (
              <>
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">Submission details</h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Abstract</span>
                      <p className="text-slate-900 mt-0.5 whitespace-pre-wrap">{documentsModalSubmission.abstract}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-slate-500">Stage</span>
                        <p className="text-slate-900 capitalize">{documentsModalSubmission.current_state}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Category</span>
                        <p className="text-slate-900">{documentsModalSubmission.technology_area || '—'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Status</span>
                        <p className="text-slate-900">{documentsModalSubmission.status}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Submitted</span>
                        <p className="text-slate-900">{formatDate(documentsModalSubmission.submission_date)}</p>
                      </div>
                    </div>
                    {documentsModalSubmission.keywords && documentsModalSubmission.keywords.length > 0 && (
                      <div>
                        <span className="text-slate-500">Keywords</span>
                        <p className="text-slate-900">{documentsModalSubmission.keywords.join(', ')}</p>
                      </div>
                    )}
                    {documentsModalSubmission.expected_outcome && (
                      <div>
                        <span className="text-slate-500">Expected outcome</span>
                        <p className="text-slate-900 whitespace-pre-wrap">{documentsModalSubmission.expected_outcome}</p>
                      </div>
                    )}
                    {(documentsModalSubmission.submitter_email || documentsModalSubmission.institution_name) && (
                      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200">
                        {documentsModalSubmission.submitter_email && (
                          <div>
                            <span className="text-slate-500">Submitter</span>
                            <p className="text-slate-900">{documentsModalSubmission.submitter_email}</p>
                          </div>
                        )}
                        {documentsModalSubmission.institution_name && (
                          <div>
                            <span className="text-slate-500">Institution</span>
                            <p className="text-slate-900">{documentsModalSubmission.institution_name}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Supporting documents
                  </h4>
                  {isAdmin && (
                    <div className="mb-4 p-3 rounded border border-slate-200 bg-white space-y-2">
                      <p className="text-xs text-slate-600">Add a document to this submission (PDF, DOC, images, ZIP up to 25MB)</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.zip"
                          className="text-sm text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            setAddDocFile(f || null);
                            setAddDocError(null);
                          }}
                        />
                        <Button
                          size="sm"
                          disabled={!addDocFile || addDocLoading}
                          onClick={async () => {
                            if (!documentsModalSubmission || !addDocFile) return;
                            setAddDocLoading(true);
                            setAddDocError(null);
                            try {
                              await uploadDocuments(documentsModalSubmission.submission_uuid, [addDocFile]);
                              const updated = await getSubmission(documentsModalSubmission.submission_uuid);
                              setDocumentsModalSubmission(updated);
                              setAddDocFile(null);
                              setSubmissions((prev) =>
                                prev.map((s) =>
                                  s.submission_uuid === updated.submission_uuid ? updated : s
                                )
                              );
                            } catch (e: unknown) {
                              const res = e && typeof e === 'object' && 'response' in e && (e as { response?: { data?: { message?: string } } }).response?.data;
                              setAddDocError(res?.message || (e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Upload failed'));
                            } finally {
                              setAddDocLoading(false);
                            }
                          }}
                        >
                          {addDocLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                          Add document
                        </Button>
                      </div>
                      {addDocError && <p className="text-xs text-red-600">{addDocError}</p>}
                    </div>
                  )}
                  {documentsModalSubmission.documents && documentsModalSubmission.documents.length > 0 ? (
                    <ul className="space-y-2">
                      {documentsModalSubmission.documents.map((doc) => (
                        <li
                          key={doc.doc_uuid}
                          className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            <span className="text-sm text-slate-900 truncate" title={doc.file_name}>
                              {doc.file_name}
                            </span>
                            <span className="text-xs text-slate-500 flex-shrink-0">
                              ({(doc.file_size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={downloadingDocId === doc.doc_uuid}
                            onClick={async () => {
                              if (!documentsModalSubmission) return;
                              setDownloadingDocId(doc.doc_uuid);
                              try {
                                await downloadDocument(
                                  documentsModalSubmission.submission_uuid,
                                  doc.doc_uuid,
                                  doc.file_name
                                );
                              } catch (e: unknown) {
                                console.error('Download failed', e);
                                const msg =
                                  e && typeof e === 'object' && 'message' in e
                                    ? String((e as { message: unknown }).message)
                                    : 'Download failed';
                                alert(msg);
                              } finally {
                                setDownloadingDocId(null);
                              }
                            }}
                          >
                            {downloadingDocId === doc.doc_uuid ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 mr-1" />
                            )}
                            Download
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="rounded border-2 border-dashed border-slate-200 bg-white flex items-center justify-center py-6 text-slate-500 text-sm">
                      No supporting documents uploaded
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
